/** The open report (item 63; item 71 made it read like a finished product).
 *
 *  - A header that stays at the top of the report while it is read: the title,
 *    the days it covers and what they are compared with, when it was ready and
 *    which model wrote it (by the name people know it by), and the actions -
 *    Download (PDF / Word / Markdown) first, then Mark as unread, the report
 *    template and Delete under "More".
 *  - The headline figures (computed by the server - report_facts - and only
 *    formatted here), each change with the same arrow and colour as the tables.
 *  - The report itself (ReportBody): its summary, its sections with a chart
 *    under the trend and the organizations, and its tables, at a comfortable
 *    reading width, in the report's own language and direction.
 *  - A report still being written, or one that could not be: what happened in
 *    words, and Try again / Edit report template. */
import { Dropdown, Spin, message as antdMessage } from 'antd';
import {
  DeleteOutlined, DownloadOutlined, EditOutlined, EyeInvisibleOutlined, LoadingOutlined, MoreOutlined, RedoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMemo, type ReactNode } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import insightsService from '../../services/insightsService';
import type { LibraryReport, ReportDetail, ReportHeadline } from '../../types/insights';
import { blobErrorCode } from '@/utils/download';
import { ReportBody } from './ReportBody';
import { parseReport } from './reportSections';
import {
  type KpiKind, instantLabel, kpiDelta, kpiValue, reportError, statusReason, windowLabel,
} from './reportParts';

const KPIS: { key: keyof ReportHeadline; kind: KpiKind; label: string }[] = [
  { key: 'conversations', kind: 'count', label: 'insights.reports.kpi.conversations' },
  { key: 'active_users', kind: 'count', label: 'insights.reports.kpi.activePeople' },
  { key: 'liked_pct', kind: 'percent', label: 'insights.reports.kpi.liked' },
  { key: 'cost_usd', kind: 'money', label: 'insights.reports.kpi.cost' },
];

interface Props {
  /** The row picked in the library (its state while it is written). */
  summary: LibraryReport | null;
  /** The report itself once open (null while loading). */
  report: ReportDetail | null;
  loading: boolean;
  onMarkUnread: (reportId: string) => void;
  onOpenTemplate: (templateId: string) => void;
  onDelete: (report: LibraryReport) => void;
  /** Write it again: the same report template, the same days. */
  onRetry: (report: LibraryReport) => void;
}

export function ReportReader({ summary, report, loading, onMarkUnread, onOpenTemplate, onDelete, onRetry }: Props) {
  const { t } = useLanguage();
  // Read once per report: the reader re-renders while the library refreshes.
  const parsed = useMemo(() => parseReport(report?.markdown || '', report?.section_keys),
    [report?.markdown, report?.section_keys]);

  if (!summary) {
    return (
      <section id="rp-reader" className="rp-reader rp-reader--none" aria-live="polite">
        <div className="rp-reader__state">
          <span className="rp-reader__state-icon" aria-hidden="true"><EyeInvisibleOutlined /></span>
          <strong>{t('insights.reports.reader.pickTitle')}</strong>
          <span>{t('insights.reports.reader.pickBody')}</span>
        </div>
      </section>
    );
  }

  const moreMenu = (r: LibraryReport, extra: { key: string; label: string; icon: ReactNode }[] = []) => ({
    items: [
      ...extra,
      ...(r.template_id ? [{ key: 'template', label: t('insights.reports.reader.template'), icon: <EditOutlined /> }] : []),
      { type: 'divider' as const },
      { key: 'delete', label: t('insights.reports.reader.delete'), icon: <DeleteOutlined />, danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'unread') onMarkUnread(r.report_id);
      if (key === 'template' && r.template_id) onOpenTemplate(r.template_id);
      if (key === 'delete') onDelete(r);
    },
  });

  const inFlight = summary.status === 'queued' || summary.status === 'running';
  if (inFlight || summary.status === 'failed' || summary.status === 'skipped') {
    return (
      <section id="rp-reader" className="rp-reader" aria-live="polite">
        <header className="rp-reader__head">
          <div className="rp-reader__titles">
            <h2 className="rp-reader__title"><bdi dir="auto">{summary.title}</bdi></h2>
            <div className="rp-reader__sub">
              <span>{t('insights.reports.reader.covers', { days: windowLabel(summary.period) })}</span>
            </div>
          </div>
          {!inFlight && (
            <div className="rp-reader__actions">
              <Dropdown trigger={['click']} menu={moreMenu(summary)} placement="bottomRight">
                <button type="button" className="cx-btn rp-icon-btn" aria-label={t('insights.reports.reader.more')}>
                  <MoreOutlined aria-hidden="true" />
                </button>
              </Dropdown>
            </div>
          )}
        </header>
        <div className={`rp-reader__state${inFlight ? '' : ' is-problem'}`}>
          {inFlight ? (
            <>
              <Spin indicator={<LoadingOutlined spin />} />
              <strong>{t('insights.reports.reader.writingTitle')}</strong>
              <span>{t('insights.reports.reader.writingBody')}</span>
            </>
          ) : (
            <>
              <span className="rp-reader__state-icon is-problem" aria-hidden="true"><WarningOutlined /></span>
              <strong>{t(summary.status === 'failed' ? 'insights.reports.reader.failedTitle'
                : 'insights.reports.reader.skippedTitle')}</strong>
              <span className="rp-reader__reason">{statusReason(t, summary.status_reason)}</span>
              {summary.template_id && (
                <div className="rp-reader__state-actions">
                  <button type="button" className="cx-btn cx-btn--primary" onClick={() => onRetry(summary)}
                          data-testid="report-try-again">
                    <RedoOutlined aria-hidden="true" /> {t('insights.reports.reader.tryAgain')}
                  </button>
                  <button type="button" className="cx-btn" onClick={() => onOpenTemplate(summary.template_id as string)}>
                    <EditOutlined aria-hidden="true" /> {t('insights.reports.reader.editTemplate')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  }

  if (loading || !report) {
    return (
      <section id="rp-reader" className="rp-reader" aria-busy="true">
        <div className="rp-reader__skeleton" aria-hidden="true">
          <span className="tpl-skel rp-skel--title" />
          <span className="tpl-skel rp-skel--line" />
          <div className="rp-skel-kpis">{KPIS.map((k) => <span key={k.key} className="tpl-skel rp-skel--kpi" />)}</div>
          <span className="tpl-skel rp-skel--para" />
          <span className="tpl-skel rp-skel--para" />
        </div>
      </section>
    );
  }

  const headline = report.facts?.headline;
  const download = async (format: 'pdf' | 'docx' | 'md') => {
    try {
      await insightsService.downloadReport(report.report_id, format);
    } catch (err) {
      // A blob response carries its JSON refusal as a blob.
      const code = await blobErrorCode(err);
      antdMessage.error(code ? reportError(t, { response: { data: { error_code: code } } })
        : t('insights.reports.errors.download'));
    }
  };

  return (
    <section id="rp-reader" className="rp-reader" aria-labelledby="rp-reader-title">
      <header className="rp-reader__head is-sticky" data-testid="report-reader-head">
        <div className="rp-reader__titles">
          <h2 id="rp-reader-title" className="rp-reader__title"><bdi dir="auto">{report.title}</bdi></h2>
          <div className="rp-reader__sub">
            <span>{t('insights.reports.reader.covers', { days: windowLabel(report.period) })}</span>
            <span>{t('insights.reports.reader.ready', { when: instantLabel(report.finished_at || report.created_at) })}</span>
            {report.model_name && (
              <span>{t('insights.reports.reader.writtenBy', { model: report.model_name })}</span>
            )}
          </div>
          {parsed.subtitle && (
            <p className="rp-reader__scope" dir={report.language === 'he' ? 'rtl' : 'ltr'} lang={report.language}>
              {parsed.subtitle}
            </p>
          )}
        </div>
        <div className="rp-reader__actions">
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                { key: 'pdf', label: t('insights.reports.reader.downloadPdf') },
                { key: 'docx', label: t('insights.reports.reader.downloadWord') },
                { key: 'md', label: t('insights.reports.reader.downloadMarkdown') },
              ],
              onClick: ({ key }) => download(key as 'pdf' | 'docx' | 'md'),
            }}
          >
            <button type="button" className="cx-btn cx-btn--primary" data-testid="report-download">
              <DownloadOutlined aria-hidden="true" /> {t('insights.reports.reader.download')}
            </button>
          </Dropdown>
          <Dropdown trigger={['click']} placement="bottomRight"
                    menu={moreMenu(report, [{ key: 'unread', label: t('insights.reports.reader.markUnread'),
                                              icon: <EyeInvisibleOutlined /> }])}>
            <button type="button" className="cx-btn rp-icon-btn" aria-label={t('insights.reports.reader.more')}
                    data-testid="report-more">
              <MoreOutlined aria-hidden="true" />
            </button>
          </Dropdown>
        </div>
      </header>

      <div className="rp-reader__page">
        {headline && (
          <div className="rp-kpis" role="list" aria-label={t('insights.reports.kpi.label')}>
            {KPIS.map(({ key, kind, label }) => {
              const delta = kpiDelta(t, headline[key], kind);
              return (
                <div key={key} className="rp-kpi" role="listitem">
                  <div className="rp-kpi__label">{t(label)}</div>
                  <div className="rp-kpi__value"><bdi>{kpiValue(headline[key], kind)}</bdi></div>
                  {delta.text && <div className={`rp-kpi__delta ${delta.tone ? `is-${delta.tone}` : ''}`}>{delta.text}</div>}
                </div>
              );
            })}
          </div>
        )}

        <ReportBody report={parsed} language={report.language} facts={report.facts as never} />
      </div>
    </section>
  );
}
